/**
 * WhatsNewModal
 * ─────────────────────────────────────────────
 * Displays categorised release notes with a precise
 * deploy timestamp in Eastern Time (EST/EDT).
 * Uses APP_NOW_UTC as the relative time reference.
 */

import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {
  formatDateTime, formatRelative, resolveDeployedAt, APP_NOW_UTC,
} from '../../constants/changelog';

const { FiX, FiZap, FiArrowRight, FiStar, FiTool, FiAlertCircle, FiClock } = FiIcons;

const CATEGORY_CONFIG = {
  feature:     { label: 'New Feature',  color: 'text-brand-pink',  bg: 'bg-brand-pink/10 border-brand-pink/25',   dot: 'bg-brand-pink'  },
  improvement: { label: 'Improvement',  color: 'text-sky-400',     bg: 'bg-sky-500/10 border-sky-500/25',         dot: 'bg-sky-400'     },
  fix:         { label: 'Bug Fix',      color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/25', dot: 'bg-emerald-400' },
};

const CategoryBadge = ({ category }) => {
  const cfg = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.improvement;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-widest ${cfg.bg} ${cfg.color}`}>
      <span className={`w-1 h-1 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const ChangeItem = ({ item, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -12 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.15 + index * 0.055, duration: 0.3, ease: 'easeOut' }}
    className="flex gap-4 py-4 border-b border-white/5 last:border-0"
  >
    <div className="text-xl flex-shrink-0 mt-0.5 w-7 text-center">{item.emoji}</div>
    <div className="flex-1 min-w-0 space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-black text-white uppercase tracking-tight">{item.title}</p>
        <CategoryBadge category={item.category} />
      </div>
      <p className="text-[12px] font-bold text-zinc-400 leading-relaxed">{item.body}</p>
    </div>
  </motion.div>
);

const WhatsNewModal = ({ entry, liveDeployment, onDismiss, onReadMore }) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onDismiss]);

  if (!entry) return null;

  // Resolve timestamp — always use APP_NOW_UTC as relative reference
  const deployedAt  = resolveDeployedAt(entry, liveDeployment);
  const dateTimeStr = formatDateTime(deployedAt);
  const relativeStr = formatRelative(deployedAt, APP_NOW_UTC);

  const features     = entry.items?.filter(i => i.category === 'feature')     || [];
  const improvements = entry.items?.filter(i => i.category === 'improvement') || [];
  const fixes        = entry.items?.filter(i => i.category === 'fix')         || [];

  const sections = [
    { key: 'feature',     items: features,     label: "🚀 What's New",   icon: FiZap         },
    { key: 'improvement', items: improvements, label: '🛠️ Improvements', icon: FiTool        },
    { key: 'fix',         items: fixes,        label: '🐛 Bug Fixes',    icon: FiAlertCircle },
  ].filter(s => s.items.length > 0);

  return (
    <AnimatePresence>
      <motion.div
        ref={overlayRef}
        className="fixed inset-0 z-[500] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => { if (e.target === overlayRef.current) onDismiss(); }}
      >
        <motion.div
          className="relative w-full max-w-lg max-h-[90vh] flex flex-col rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(160deg, rgba(28,16,42,0.99) 0%, rgba(12,8,20,0.99) 100%)',
            border: '1px solid rgba(255,102,204,0.18)',
            boxShadow: '0 40px 100px rgba(0,0,0,0.8), 0 0 80px rgba(255,102,204,0.06), inset 0 1px 0 rgba(255,255,255,0.07)',
          }}
          initial={{ opacity: 0, scale: 0.92, y: 24 }}
          animate={{ opacity: 1, scale: 1,    y: 0  }}
          exit={{   opacity: 0, scale: 0.94,  y: 12 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Ambient glow */}
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(ellipse, rgba(255,102,204,0.12) 0%, transparent 70%)', filter: 'blur(20px)' }}
          />

          {/* Header */}
          <div
            className="relative flex-shrink-0 px-7 pt-7 pb-5"
            style={{ background: 'linear-gradient(180deg, rgba(255,102,204,0.06) 0%, transparent 100%)' }}
          >
            <button
              onClick={onDismiss}
              className="absolute top-5 right-5 w-8 h-8 rounded-xl flex items-center justify-center text-zinc-600 hover:text-white hover:bg-white/8 transition-all"
            >
              <SafeIcon icon={FiX} className="text-sm" />
            </button>

            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="space-y-3"
            >
              <div className="flex items-center gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-brand-pink/25 to-brand-coral/15 border border-brand-pink/25 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiStar} className="text-brand-pink text-base" />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] font-black uppercase tracking-widest text-brand-pink">What's New</span>
                    <span className="px-2 py-0.5 rounded-full bg-brand-pink/15 border border-brand-pink/25 text-[9px] font-black text-brand-pink">
                      v{entry.version}
                    </span>
                  </div>
                  {/* EST/EDT timestamp + relative */}
                  {dateTimeStr && (
                    <div className="flex items-center gap-1.5">
                      <SafeIcon icon={FiClock} className="text-zinc-600 text-[9px]" />
                      <span className="text-[10px] font-bold text-zinc-500">
                        {dateTimeStr}
                        {relativeStr && (
                          <span className="text-zinc-700 ml-1.5">({relativeStr})</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-black tracking-tighter text-white leading-tight">{entry.headline}</h2>
                <p className="text-[12px] font-bold text-zinc-500 mt-1.5 leading-relaxed">{entry.summary}</p>
              </div>
            </motion.div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-7 pb-2 space-y-1">
            {sections.map((section, si) => (
              <div key={section.key}>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 + si * 0.05 }}
                  className="flex items-center gap-2 pt-4 pb-1"
                >
                  <p className="text-[10px] font-black uppercase tracking-widest text-zinc-600">{section.label}</p>
                  <div className="flex-1 h-px bg-white/5" />
                </motion.div>
                {section.items.map((item, idx) => (
                  <ChangeItem key={item.title} item={item} index={si * 4 + idx} />
                ))}
              </div>
            ))}
          </div>

          {/* Footer CTA */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex-shrink-0 flex items-center gap-3 px-7 py-5 border-t border-white/5"
            style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.4) 0%, transparent 100%)' }}
          >
            <button
              onClick={onDismiss}
              className="flex-1 py-3 rounded-2xl border border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700 transition-all text-[11px] font-black uppercase tracking-widest"
            >
              Got it
            </button>
            {onReadMore && (
              <button
                onClick={() => { onDismiss(); onReadMore(); }}
                className="flex-1 flex items-center justify-center gap-2 py-3 btn-brand rounded-2xl text-[11px]"
              >
                Full Changelog <SafeIcon icon={FiArrowRight} className="text-xs" />
              </button>
            )}
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WhatsNewModal;